/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import { useEffect, useRef, useCallback } from 'react'
import { clearSession } from '@az/auth-ping'
import { IDLE_TIMEOUT_MINUTES } from '../constants'

export const useIdleTimeout = () => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const idleTimeout = IDLE_TIMEOUT_MINUTES * 60 * 1000

  const forceLogoutCheck = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current
    if (timeSinceLastActivity >= idleTimeout) {
      clearSession()
      return true
    }
    return false
  }, [idleTimeout])

  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now()

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      clearSession()
    }, idleTimeout)
  }, [idleTimeout])

  const clearIdleTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      intervalRef.current = setInterval(() => {
        forceLogoutCheck()
      }, 10000)
      return
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    if (!forceLogoutCheck()) {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current
      const remainingTime = Math.max(0, idleTimeout - timeSinceLastActivity)

      if (remainingTime === 0) {
        clearSession()
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          clearSession()
        }, remainingTime)
      }
    }
  }, [idleTimeout, forceLogoutCheck])

  useEffect(() => {
    const desktopEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'click',
      'keydown',
      'wheel'
    ]
    const touchEvents = [
      'touchstart',
      'touchmove',
      'touchend',
      'touchcancel',
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel'
    ]
    const otherEvents = [
      'orientationchange',
      'focus',
      'blur',
      'resize',
      'devicemotion',
      'deviceorientation'
    ]

    const resetTimeoutHandler = () => {
      resetTimeout()
    }

    resetTimeout()

    intervalRef.current = setInterval(() => {
      if (!document.hidden) {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current
        if (timeSinceLastActivity >= idleTimeout) {
          clearSession()
        }
      }
    }, 30000)

    desktopEvents.forEach(event => {
      document.addEventListener(event, resetTimeoutHandler, true)
    })

    touchEvents.forEach(event => {
      try {
        document.addEventListener(event, resetTimeoutHandler, { passive: true, capture: true })
      } catch (e) {
        document.addEventListener(event, resetTimeoutHandler, true)
      }
    })

    otherEvents.forEach(event => {
      document.addEventListener(event, resetTimeoutHandler, true)
    })

    document.addEventListener('visibilitychange', handleVisibilityChange, true)

    const windowEvents = ['touchstart', 'touchmove', 'pointerdown', 'pointermove']
    windowEvents.forEach(event => {
      try {
        window.addEventListener(event, resetTimeoutHandler, { passive: true })
      } catch (e) {
        window.addEventListener(event, resetTimeoutHandler, false)
      }
    })

    return () => {
      clearIdleTimeout()

      desktopEvents.forEach(event => {
        document.removeEventListener(event, resetTimeoutHandler, true)
      })

      touchEvents.forEach(event => {
        try {
          document.removeEventListener(event, resetTimeoutHandler, {
            passive: true,
            capture: true
          } as any)
        } catch (e) {
          document.removeEventListener(event, resetTimeoutHandler, true)
        }
      })

      otherEvents.forEach(event => {
        document.removeEventListener(event, resetTimeoutHandler, true)
      })

      document.removeEventListener('visibilitychange', handleVisibilityChange, true)

      windowEvents.forEach(event => {
        try {
          window.removeEventListener(event, resetTimeoutHandler, { passive: true } as any)
        } catch (e) {
          window.removeEventListener(event, resetTimeoutHandler, false)
        }
      })
    }
  }, [resetTimeout, clearIdleTimeout, handleVisibilityChange, idleTimeout])

  return { resetTimeout, clearIdleTimeout }
}
